// Per-profile datasets for the Attribution / Journeys / Funnels surfaces.
// Each profile publishes the same shape so the preview handlers can look up
// one blob by profile key and slice it into API responses.
//
// The pharma numbers come from the ANA-1003 appendix (Zephyra brand,
// Verenta manufacturer site, Calderis fulfillment partner). Healthcare
// numbers reproduce the shape that the seeded demo env has been showing —
// a reviewer comparing screenshots should not spot a difference for the
// healthcare profile aside from Meridian-flavored labels.
//
// Reviewer note: every derived aggregate here is spelled out (impressions,
// mid-funnel, low-funnel counts per platform; per-campaign conversions +
// spend + CPA; per-path conversion counts) precisely because the ticket
// mandates the tables must reconcile on the same screen. Do not switch to
// randomized synthesis without also updating the reconciliation tests.

import type { PreviewProfileKey } from "../profiles";
import type { RecommendationCardResponse } from "@/api/insights";
import { PREVIEW_ENV_ID } from "../runtime";

export type AttributionPlatformKey =
  | "google_ads"
  | "meta_ads"
  | "the_trade_desk";

export interface AttributionCampaign {
  key: string;
  label: string;
  platform: AttributionPlatformKey;
  conversions: number;
  spendCents: number;
  cpaCents: number;
}

export interface AttributionSourceStageMix {
  google: number;
  meta: number;
  dsp: number;
}

export interface AttributionStageTotals {
  impressions: number;
  landingPageViews: number;
  midFunnelConversions: number;
  lowFunnelConversions: number;
}

export interface AttributionTopJourney {
  path: string; // display label, e.g. "Google Ads → Direct → Conversion"
  channels: string[]; // ordered channels contributing to this journey
  conversions: number;
}

export interface AttributionSubMetricTiles {
  roasLastPeriod: number;
  roasDifference: number;
  totalLastPeriod: number;
  totalDifference: number;
}

export interface AttributionSummary {
  totalConversions: number;
  avgPathLength: number;
  pctMultiChannel: number; // 0..100
  avgTimeToDays: number;
  spendCents: number;
  conversionRatePct: number;
  roasPct: number;
}

export interface PreviewFunnelSpec {
  id: string;
  name: string;
  description: string;
  steps: { label: string; urlPatterns: string[] }[];
  stepCounts: number[];
  createdAtDaysAgo: number;
}

export interface AttributionDataset {
  brandLabel: string;
  summary: AttributionSummary;
  stageTotals: AttributionStageTotals;
  stageSourceMix: Record<
    keyof AttributionStageTotals,
    AttributionSourceStageMix
  >;
  campaigns: AttributionCampaign[];
  topJourneys: AttributionTopJourney[];
  touchpointDistribution: {
    single: number;
    twoTouch: number;
    threePlusTouch: number;
  };
  subMetricTiles: AttributionSubMetricTiles;
  funnels: PreviewFunnelSpec[];
}

// -----------------------------------------------------------------------------
// PHARMA
// -----------------------------------------------------------------------------

const PHARMA_DATASET: AttributionDataset = {
  brandLabel: "Zephyra",
  summary: {
    totalConversions: 520,
    avgPathLength: 2.4,
    pctMultiChannel: 68,
    avgTimeToDays: 3.8,
    spendCents: 28_000 * 100,
    conversionRatePct: 5.2,
    // 520 conversions / $28,000 spend * $100 average order value → 185% ROAS
    // (rounded to 186 for tile aesthetics; SE-signed-off approximate).
    roasPct: 186,
  },
  stageTotals: {
    impressions: 540_000,
    landingPageViews: 11_600,
    midFunnelConversions: 2_970,
    lowFunnelConversions: 520,
  },
  stageSourceMix: {
    impressions: { google: 0, meta: 0, dsp: 100 },
    landingPageViews: { google: 68, meta: 32, dsp: 0 },
    midFunnelConversions: { google: 74, meta: 26, dsp: 0 },
    lowFunnelConversions: { google: 72, meta: 28, dsp: 0 },
  },
  campaigns: [
    {
      key: "zephyra_brand_search",
      label: "Zephyra_Brand_Search",
      platform: "google_ads",
      conversions: 280,
      spendCents: 12_000 * 100,
      cpaCents: 4286,
    },
    {
      key: "zephyra_retargeting",
      label: "Zephyra_Retargeting",
      platform: "meta_ads",
      conversions: 100,
      spendCents: 5_000 * 100,
      cpaCents: 5000,
    },
    {
      key: "zephyra_nonbrand_generic",
      label: "Zephyra_NonBrand_Generic",
      platform: "google_ads",
      conversions: 95,
      spendCents: 8_000 * 100,
      cpaCents: 8421,
    },
    {
      key: "zephyra_condition_interest",
      label: "Zephyra_Condition_Interest",
      platform: "meta_ads",
      conversions: 28,
      spendCents: 1_800 * 100,
      cpaCents: 6429,
    },
    {
      key: "zephyra_prospecting_broad",
      label: "Zephyra_Prospecting_Broad",
      platform: "meta_ads",
      conversions: 17,
      spendCents: 1_200 * 100,
      cpaCents: 7059,
    },
  ],
  // Spans touch counts 1-5 so the Journey Flow sankey has a real chain to
  // render at every TOUCHES button, not just the shortest paths (see
  // ANA-1003's "no dead-end empty state" AC). handleTopJourneys/
  // handleChannelContribution/handleJourneySankey are all derived from this
  // list, so entries referenced by exact conversion count in
  // handlers.test.ts (Google Ads → Direct: 145, Meta Ads → Direct: 48,
  // Direct: 42) must not change; the remaining counts were rebalanced
  // around them to keep the 520 total intact.
  topJourneys: [
    {
      path: "Google Ads → Direct → Conversion",
      channels: ["Google Ads", "Direct"],
      conversions: 145,
    },
    {
      path: "Google Ads → Google Ads → Conversion",
      channels: ["Google Ads", "Google Ads"],
      conversions: 80,
    },
    {
      path: "Meta Ads → Google Ads → Conversion",
      channels: ["Meta Ads", "Google Ads"],
      conversions: 55,
    },
    {
      path: "Meta Ads → Direct → Conversion",
      channels: ["Meta Ads", "Direct"],
      conversions: 48,
    },
    {
      path: "Direct → Conversion",
      channels: ["Direct"],
      conversions: 42,
    },
    {
      path: "Google Ads → Meta Ads → Direct → Conversion",
      channels: ["Google Ads", "Meta Ads", "Direct"],
      conversions: 45,
    },
    {
      path: "Meta Ads → Google Ads → Direct → Conversion",
      channels: ["Meta Ads", "Google Ads", "Direct"],
      conversions: 25,
    },
    {
      path: "Google Ads → Google Ads → Meta Ads → Direct → Conversion",
      channels: ["Google Ads", "Google Ads", "Meta Ads", "Direct"],
      conversions: 15,
    },
    {
      path: "Google Ads → Meta Ads → Google Ads → Meta Ads → Direct → Conversion",
      channels: ["Google Ads", "Meta Ads", "Google Ads", "Meta Ads", "Direct"],
      conversions: 7,
    },
    {
      path: "Other paths → Conversion",
      channels: ["Other"],
      conversions: 58,
    },
  ],
  touchpointDistribution: { single: 32, twoTouch: 45, threePlusTouch: 23 },
  subMetricTiles: {
    // Approximate pharma tile values, kept close to the healthcare
    // defaults' magnitudes so the layout doesn't jump between profiles.
    roasLastPeriod: 172,
    roasDifference: 14,
    totalLastPeriod: 25_000,
    totalDifference: 12,
  },
  funnels: [
    {
      id: "pharma-funnel-end-to-end",
      name: "Zephyra: End-to-End (Verenta → Calderis)",
      description:
        "Full journey from a Zephyra landing page visit through Calderis conversion.",
      steps: [
        { label: "Zephyra Landing Page", urlPatterns: ["*/zephyra"] },
        {
          label: '"Get Zephyra" CTA Click',
          urlPatterns: ["*/zephyra/get-started*"],
        },
        {
          label: "Calderis: Start Checkout",
          urlPatterns: ["*/calderis/checkout"],
        },
        {
          label: "Calderis: Contact Information",
          urlPatterns: ["*/calderis/checkout/contact"],
        },
        {
          label: "Calderis: Medical Questionnaire",
          urlPatterns: ["*/calderis/checkout/questionnaire"],
        },
        {
          label: "Calderis: Provider Selection",
          urlPatterns: ["*/calderis/checkout/provider"],
        },
        {
          label: "Calderis: Payment",
          urlPatterns: ["*/calderis/checkout/payment"],
        },
        {
          label: "Calderis: Converted",
          urlPatterns: ["*/calderis/checkout/confirmation*"],
        },
      ],
      stepCounts: [10_000, 4_200, 3_800, 2_900, 1_800, 1_200, 680, 520],
      createdAtDaysAgo: 30,
    },
    {
      id: "pharma-funnel-redirect",
      name: "Zephyra: Verenta → Calderis Redirect",
      description:
        "Cross-domain handoff from the Verenta CTA through Calderis Start Checkout.",
      steps: [
        {
          label: '"Get Zephyra" CTA Click',
          urlPatterns: ["*/zephyra/get-started*"],
        },
        {
          label: "Calderis page loaded (post-redirect)",
          urlPatterns: ["*/calderis"],
        },
        {
          label: "Calderis: Start Checkout",
          urlPatterns: ["*/calderis/checkout"],
        },
      ],
      stepCounts: [4_200, 4_183, 3_800],
      createdAtDaysAgo: 20,
    },
    {
      id: "pharma-funnel-condensed",
      name: "Zephyra: Condensed (Site Visit → Converted)",
      description: "Zephyra site visit through Calderis conversion, condensed.",
      steps: [
        { label: "Zephyra Site Visit", urlPatterns: ["*/zephyra*"] },
        {
          label: "Calderis Checkout Started",
          urlPatterns: ["*/calderis/checkout"],
        },
        {
          label: "Calderis Converted",
          urlPatterns: ["*/calderis/checkout/confirmation*"],
        },
      ],
      stepCounts: [10_000, 3_800, 520],
      createdAtDaysAgo: 10,
    },
  ],
};

// Weekly Recommendations content for the pharma profile. Every number here
// is derived from PHARMA_DATASET above (campaign spend/conversions/CPA,
// stageTotals, the end-to-end funnel's stepCounts) rather than invented
// figures, so a reviewer cross-checking a card's supportingMetrics against
// the Campaigns table or the Zephyra funnel above will find the same
// numbers. Month-over-month and cost-comparison framing (e.g. "+18%") is
// standalone narrative color, same as the real /v2 endpoint's demo dataset
// (see back/insights/internal/db/demo_data_datasets/*/recommendation_cards.json)
// - there's no other preview-mode surface showing week-over-week deltas to
// reconcile against.
const PHARMA_RECOMMENDATION_CARDS: RecommendationCardResponse[] = [
  {
    cardId: "pharma-rec-brand-search-trend",
    envId: PREVIEW_ENV_ID,
    accountId: PREVIEW_ENV_ID,
    cardRank: 1,
    generatedAt: "2026-08-31T08:00:00Z",
    dateRangeEnd: "2026-08-31T00:00:00Z",
    timeWindowUsed: "30d",
    attributionModelUsed: "last_touch",
    attributionModelReason:
      "Last-touch isolates Zephyra_Brand_Search's own closing performance for the month-over-month comparison.",
    title:
      "Zephyra_Brand_Search Growing 18% Month-Over-Month, Now Over Half of All Conversions",
    teaser:
      "Zephyra_Brand_Search grew 18% versus the prior 30-day window and now drives 54% of all Zephyra conversions.",
    fullRecommendation:
      "Zephyra_Brand_Search grew from roughly 237 to 280 conversions over the past two 30-day windows (+18%) and now accounts for 54% of all Zephyra conversions. Consider raising its daily budget cap so it doesn't get spend-limited during peak search volume, before shifting focus to lower-performing campaigns.",
    shortRecommendation:
      "Zephyra_Brand_Search conversions grew 18% month-over-month - raise its budget cap to avoid spend limits.",
    supportingMetrics: [
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra_Brand_Search conversions (last 30 days)",
        value: "280",
        definition:
          "Bottom-funnel conversions attributed to Zephyra_Brand_Search via last-touch: 280",
      },
      {
        dateRange: {
          begin: "2026-07-02T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Month-over-month growth",
        value: "+18%",
        definition:
          "280 conversions vs. approximately 237 in the prior 30-day window = +18%",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Share of all Zephyra conversions",
        value: "54%",
        definition:
          "Zephyra_Brand_Search conversions (280) / total Zephyra conversions (520) = 54%",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra_Brand_Search acquisition cost",
        value: "$42.86",
        definition: "Platform spend ($12,000) / conversions (280) = $42.86",
      },
    ],
    changeStatus: "new",
    changeNote:
      "First recommendation card generated for this account; no prior week to compare against.",
    dataQualityLabel: "High",
    dataQualityReason:
      "280 conversions over 30 days is well above the reporting threshold, with complete spend data.",
    category: "Trend",
    bottomFunnelEvents: ["Calderis: Converted"],
    midFunnelEvents: ['"Get Zephyra" CTA Click', "Calderis: Start Checkout"],
  },
  {
    cardId: "pharma-rec-prospecting-budget",
    envId: PREVIEW_ENV_ID,
    accountId: PREVIEW_ENV_ID,
    cardRank: 2,
    generatedAt: "2026-08-31T08:00:00Z",
    dateRangeEnd: "2026-08-31T00:00:00Z",
    timeWindowUsed: "30d",
    attributionModelUsed: "last_touch",
    attributionModelReason:
      "Last-touch attribution isolates each campaign's own closing cost for a direct CPA comparison.",
    title:
      "Zephyra_Prospecting_Broad Costs 65% More Per Conversion Than Zephyra_Brand_Search",
    teaser:
      "Zephyra_Prospecting_Broad's $70.59 CPA is 65% higher than Zephyra_Brand_Search's $42.86.",
    fullRecommendation:
      "Zephyra_Prospecting_Broad spends $1,200 to acquire 17 conversions at $70.59 each - 65% more than Zephyra_Brand_Search's $42.86 CPA. Shift Prospecting Broad's budget to Brand Search, which is already the top-performing campaign and has room to scale before hitting diminishing returns.",
    shortRecommendation:
      "Shift Zephyra_Prospecting_Broad's $1,200 budget to Zephyra_Brand_Search.",
    supportingMetrics: [
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra_Brand_Search acquisition cost",
        value: "$42.86",
        definition: "Platform spend ($12,000) / conversions (280) = $42.86",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra_Prospecting_Broad acquisition cost",
        value: "$70.59",
        definition: "Platform spend ($1,200) / conversions (17) = $70.59",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Cost premium",
        value: "+65%",
        definition: "($70.59 - $42.86) / $42.86 = +65%",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra_Prospecting_Broad conversions",
        value: "17",
        definition: "Bottom-funnel conversions attributed via last-touch: 17",
      },
    ],
    changeStatus: "new",
    changeNote:
      "First recommendation card generated for this campaign pairing.",
    dataQualityLabel: "Medium",
    dataQualityReason:
      "Zephyra_Prospecting_Broad has only 17 conversions over 30 days - a directional signal, not yet a high-confidence sample.",
    category: "Budget",
    bottomFunnelEvents: ["Calderis: Converted"],
    midFunnelEvents: ['"Get Zephyra" CTA Click'],
  },
  {
    cardId: "pharma-rec-payment-dropoff",
    envId: PREVIEW_ENV_ID,
    accountId: PREVIEW_ENV_ID,
    cardRank: 3,
    generatedAt: "2026-08-31T08:00:00Z",
    dateRangeEnd: "2026-08-31T00:00:00Z",
    timeWindowUsed: "30d",
    attributionModelUsed: "linear",
    attributionModelReason:
      "Linear attribution spreads credit evenly across the multi-step Calderis checkout flow, matching the funnel's step-by-step framing.",
    title:
      "Calderis Payment Step Loses 43% of Prospects - the Funnel's Largest Drop-Off",
    teaser:
      "Of the 1,200 prospects who reach Calderis: Provider Selection, only 680 continue to Calderis: Payment.",
    fullRecommendation:
      "Of the 1,200 prospects who reach Calderis: Provider Selection, only 680 continue to Calderis: Payment - a 43% drop, the steepest of any step in the Zephyra funnel. Audit the Payment step for friction: form length, accepted payment methods, and unexpected fees are the most common causes at this stage.",
    shortRecommendation:
      "Audit the Calderis Payment step - it's the funnel's largest drop-off (43%).",
    supportingMetrics: [
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Provider Selection to Payment drop-off",
        value: "43%",
        definition: "(1,200 - 680) / 1,200 = 43%",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Prospects lost at Payment step",
        value: "520",
        definition:
          "1,200 reaching Provider Selection minus 680 reaching Payment = 520",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Payment to Converted drop-off",
        value: "23%",
        definition: "(680 - 520) / 680 = 23%, for comparison",
      },
      {
        dateRange: {
          begin: "2026-08-01T00:00:00Z",
          end: "2026-08-31T00:00:00Z",
        },
        metric: "Zephyra: End-to-End funnel completion rate",
        value: "5.2%",
        definition: "520 converted / 10,000 landing page visits = 5.2%",
      },
    ],
    changeStatus: "new",
    changeNote:
      "First recommendation card generated for the Zephyra: End-to-End funnel.",
    dataQualityLabel: "High",
    dataQualityReason:
      "The funnel has 1,200+ sessions reaching Provider Selection over the full 30-day window.",
    category: "Funnel & Journey",
    bottomFunnelEvents: ["Calderis: Converted"],
    midFunnelEvents: ["Calderis: Provider Selection", "Calderis: Payment"],
  },
];

export function getPharmaRecommendationCards(): RecommendationCardResponse[] {
  return PHARMA_RECOMMENDATION_CARDS;
}

// -----------------------------------------------------------------------------
// HEALTHCARE
// -----------------------------------------------------------------------------

// The healthcare dataset intentionally keeps the same shape as pharma but
// with Meridian-flavored labels and numbers close to the seeded demo env's
// figures. It powers Attribution / Journeys preview handlers where no
// pre-existing graph-derived numbers are available. Funnels are still
// served from the synthetic PreviewGraph — the funnels array here is used
// only when a caller explicitly asks the dataset for them (e.g. for tests).

const HEALTHCARE_DATASET: AttributionDataset = {
  brandLabel: "Meridian Health System",
  summary: {
    totalConversions: 1_250,
    avgPathLength: 1.9,
    pctMultiChannel: 54,
    avgTimeToDays: 4.6,
    spendCents: 74_500 * 100,
    conversionRatePct: 3.4,
    roasPct: 73,
  },
  stageTotals: {
    impressions: 1_400_000,
    landingPageViews: 36_800,
    midFunnelConversions: 6_400,
    lowFunnelConversions: 1_250,
  },
  stageSourceMix: {
    impressions: { google: 0, meta: 0, dsp: 100 },
    landingPageViews: { google: 55, meta: 30, dsp: 0 },
    midFunnelConversions: { google: 58, meta: 30, dsp: 0 },
    lowFunnelConversions: { google: 60, meta: 28, dsp: 0 },
  },
  campaigns: [
    {
      key: "meridian_brand_search",
      label: "Meridian_Brand_Search",
      platform: "google_ads",
      conversions: 480,
      spendCents: 22_000 * 100,
      cpaCents: 4583,
    },
    {
      key: "meridian_cardiology",
      label: "Meridian_Cardiology_NonBrand",
      platform: "google_ads",
      conversions: 260,
      spendCents: 18_000 * 100,
      cpaCents: 6923,
    },
    {
      key: "meridian_maternity",
      label: "Meridian_Maternity_Retargeting",
      platform: "meta_ads",
      conversions: 220,
      spendCents: 12_000 * 100,
      cpaCents: 5455,
    },
    {
      key: "meridian_insurance_landing",
      label: "Meridian_Insurance_Landing",
      platform: "meta_ads",
      conversions: 130,
      spendCents: 9_500 * 100,
      cpaCents: 7308,
    },
    {
      key: "meridian_urgent_care",
      label: "Meridian_UrgentCare_Local",
      platform: "google_ads",
      conversions: 160,
      spendCents: 13_000 * 100,
      cpaCents: 8125,
    },
  ],
  topJourneys: [
    {
      path: "Google Ads → Direct → Conversion",
      channels: ["Google Ads", "Direct"],
      conversions: 340,
    },
    {
      path: "Direct → Conversion",
      channels: ["Direct"],
      conversions: 275,
    },
    {
      path: "Meta Ads → Direct → Conversion",
      channels: ["Meta Ads", "Direct"],
      conversions: 195,
    },
    {
      path: "Google Ads → Google Ads → Conversion",
      channels: ["Google Ads", "Google Ads"],
      conversions: 160,
    },
    {
      path: "Meta Ads → Google Ads → Conversion",
      channels: ["Meta Ads", "Google Ads"],
      conversions: 120,
    },
    {
      path: "Other paths → Conversion",
      channels: ["Other"],
      conversions: 160,
    },
  ],
  touchpointDistribution: { single: 46, twoTouch: 38, threePlusTouch: 16 },
  // Kept identical to demoData.ts's pre-ticket values so preview-off
  // behavior is unchanged.
  subMetricTiles: {
    roasLastPeriod: 73,
    roasDifference: 3,
    totalLastPeriod: 80_000,
    totalDifference: -6.7,
  },
  // The healthcare profile uses graph-derived funnels via the funnels
  // handler; this list is only referenced by direct dataset consumers.
  funnels: [],
};

const DATASETS: Record<PreviewProfileKey, AttributionDataset> = {
  healthcare: HEALTHCARE_DATASET,
  pharma: PHARMA_DATASET,
};

export function getAttributionDataset(
  profile: PreviewProfileKey,
): AttributionDataset {
  return DATASETS[profile];
}

// Sums the campaign conversions/spend for a single platform. Exposed for
// handler use so paid-rollup totals reconcile with the campaign table.
export function platformRollup(
  dataset: AttributionDataset,
  platform: AttributionPlatformKey,
): { conversions: number; spendCents: number; cpaCents: number } {
  const rows = dataset.campaigns.filter((c) => c.platform === platform);
  const conversions = rows.reduce((s, r) => s + r.conversions, 0);
  const spendCents = rows.reduce((s, r) => s + r.spendCents, 0);
  const cpaCents = conversions === 0 ? 0 : Math.round(spendCents / conversions);
  return { conversions, spendCents, cpaCents };
}
